import * as React from 'react';
import './index.css';
import './theme/cubi.css';
import PixiApp from './containers/pixi-app';
import FlatWorld from './world';
import LevelManager, { LoadingTypes } from './levels';
import RenderUtils from './render_utils';
import WorkspaceManager from './workspace';
import Analytics from './analytics';
import Sprite from './sprite';
import MenuAppBar from './containers/Menu';
import NewVariableModal from './containers/NewVariableModal';
import RenameVariableModal from './containers/RenameVariableModal';
import SpriteEditor from './containers/SpriteEditor';
import SplitPane from 'react-split-pane';
import environment from './environment';
import { Logging } from './utils/logging';
import { Hidden } from '@material-ui/core';
import withWidth from '@material-ui/core/withWidth';
import LoadingOverlay, { endLoading, withLoading } from './containers/LoadingOverlay';
import { OptionsObject as NotiStackOptionsObject, withSnackbar, WithSnackbarProps } from 'notistack';
import Toolbox from './toolbox';
import { Restore, RestoreCostumeAddStatus } from "./utils/restore";
import { translate } from './i18n/i18n';
import { SpriteEditorInstance } from "./containers/SpriteEditor/SpriteEditor";
import i18n from "i18next";
import { SessionStorage } from './utils/storage';
import { WithTranslation, withTranslation } from "react-i18next";
import GamemasterModal from "./containers/GamemasterModal";
import { Blockly } from './blocks';
import Utils from './api/utils';

export const notifyUser = (message: string, options?: NotiStackOptionsObject) => AppRoot.INSTANCE.props.enqueueSnackbar(message, options);
Logging.initialize(notifyUser);
Restore.initialize();

const fullHeightStyle = { height: '100%' };

interface AppRootProps extends WithSnackbarProps, WithTranslation {

}

export class AppRoot extends (React.Component)<AppRootProps> {

    static INSTANCE: AppRoot;
    state: {
        sprite: Sprite | null,
        playing: boolean,
        loading: boolean,
    } = {
            sprite: null,
            playing: false,
            loading: true
        };
    view: string = "normal";
    levelManager: LevelManager | null;
    workspaceManager: WorkspaceManager | null;
    world: FlatWorld;
    analytics: Analytics;
    pixiApp: React.RefObject<PixiApp>;

    constructor(props: any) {
        super(props);

        AppRoot.INSTANCE = this;

        this.levelManager = null;
        this.workspaceManager = null;
        this.pixiApp = React.createRef();
        this.world = new FlatWorld(1 / window.devicePixelRatio);
        this.analytics = new Analytics();

        (window as any).world = this.world;
        (window as any).analytics = this.analytics;
        (window as any).logging = Logging;
        (window as any).restore = Restore;

        (window as any).setSelectedSprite = this.setSelectedSprite;
        (window as any).getSelectedSprite = () => this.state.sprite;
    }

    @withLoading({ autoEnd: false })
    componentDidMount() {
        Logging.info(`Booting ${environment.APP_NAME}...`);

        (window as any).renderUtils = new RenderUtils(this.world, this.pixiApp.current!.getRenderer());
        this.pixiApp.current!.windowResized();

        // Create workspaceManager at location blocklyArea.
        this.workspaceManager = new WorkspaceManager('blocklyArea',
            this.world);
        (window as any).workspaceManager = this.workspaceManager;
        this.workspaceManager.onActiveWorkspaceChanged = (ws, sprite) => this.setSelectedSprite(sprite);

        // Load level
        this.levelManager = new LevelManager(this.workspaceManager, this.world);
        this.levelManager.onEmpty = () => this.setSelectedSprite(null);
        (window as any).levelManager = this.levelManager;

        //Load level from URL or default level.
        const loadInfo = {
            type: LoadingTypes.URL_JSON,
            buildMode: false
        };
        const urlParams = new URLSearchParams(window.location.search);
        const queryLevelUrl = urlParams.get('level') || '';
        const levelURL = window.atob(queryLevelUrl) || 'https://levels.iw.it-for-kids.org/Templates/Berufe_entdecken.cubi';
        const teacher = urlParams.get('teacher');
        const embedded = urlParams.get('embedded');
        const debug = urlParams.get('debug');
        if (teacher || SessionStorage.get('SkipPassword')) {
            loadInfo.buildMode = true;
            teacher && SessionStorage.set('SkipPassword', '1');
        }
        if (embedded) {
            this.view = 'embedded';
        } else if (debug) {
            this.view = 'debug';
        }
        (window as any).app_onResizeSplitPanel = this.onResizeSplitPanel;
        (window as any).addEventListener('message', this.loadProjectFromParent);
        this.levelManager.importFile(levelURL, loadInfo, undefined, () => {
            //Despite using the level finsihed callback, we need to wait some more time.
            //This is a BUG. I assume that Blockly (or maybe even our code) is doing some background work async so that
            //no blocks are present when the callback is called.
            //Using setTimeout(..., 0) sets the this.runProject() to the current bottom of the javascript stack, so that we are queued after all other async functions.)
            setTimeout(() => {
                ((window as any).renderUtils as RenderUtils).generateColorPalette();
                this.onResizeSplitPanel();
                endLoading();
            }, 0);
        });
    }

    /**
     * responsible for loading a level sent via <postMessage> when inside an iframe
     */
    private loadProjectFromParent(event: MessageEvent) {
        if (!event.data || event.origin === (window as any).location.origin) return;
        (window as any).levelManager.importFile(event.data, { type: LoadingTypes.DIRECT_JSON }, undefined, () => {
            setTimeout(() => {
                ((window as any).renderUtils as RenderUtils).generateColorPalette();
                (window as any).app_onResizeSplitPanel();
                endLoading();
            }, 0);
        });
    }

    // runs all workspaces and adds 1 count for analytics
    runProject = async () => {
        (window as any).Utils = Utils
        this.workspaceManager!.workspaces.forEach(workspace => {
            workspace.blocklyWorkspace.getBlocksByType('play_audio', false).forEach((block: any) => {
                let url = Blockly.JavaScript.valueToCode(block, 'URL', Blockly.JavaScript.ORDER_COMMA) || '';
                url = url.replaceAll("'","") // possibly errors due to ' in the middle that are intended
                if (!Utils.audioFiles[url]) {
                    try {
                        let audioPlayer = new Audio(url)
                        // audioPlayer.onloadeddata = () => console.warn('loaded');
                        Utils.audioFiles[url] = {
                            audioPlayer: audioPlayer,
                            loaded: new Promise(resolve => {
                                audioPlayer.oncanplaythrough = () => resolve()
                                audioPlayer.onerror = () => resolve()
                            }),
                        };

                        audioPlayer.onerror = () => {
                            notifyUser("Wrong Url: " + url , {variant: 'error'})
                            delete Utils.audioFiles[url]
                        }
                    } catch(e){
                        delete Utils.audioFiles[url]
                        console.error(e)
                    }
                }
            });
        });
        for (const audioFile of Object.values(Utils.audioFiles))
        {
            await (audioFile.loaded);
        }

        this.setState({
            playing: true
        });
        this.world.setVisibility(1);
        this.workspaceManager!.api.sensing.ignoreAllKeyInputs(false);
        if (this.workspaceManager!.codeRunner.isPaused()) {
            this.workspaceManager!.codeRunner.unpause();
        } else {
            this.levelManager!.reset();
            this.workspaceManager!.runStart();
            this.analytics.addProjectExecutionsCount();
        }
    };

    pauseProject = () => {

        for (const audioFile of Object.values(Utils.audioFiles))
        {
            audioFile.audioPlayer.pause();
        }

        this.workspaceManager!.api.sensing.ignoreAllKeyInputs(true);
        this.world.setVisibility(0.4);
        //removed the sprite position reset because it is not really necessary for the users since sprites cant leave the boundaries
        this.setState({
            playing: false
        });
        SpriteEditorInstance.INSTANCE.updateDragging();
        SpriteEditorInstance.readNewValues();
        this.workspaceManager!.codeRunner.pause();
    };

    stopProject = () => {
        this.world.setVisibility(0.1);
        this.setState({
            playing: false
        });
        this.workspaceManager!.codeRunner.stop();
    }

    resetProject = () => {

        for (const audioFile of Object.values(Utils.audioFiles))
        {
            audioFile.audioPlayer.pause();
            audioFile.audioPlayer.currentTime = 0;
        }

        this.setState({
            playing: false
        });
        this.world.setVisibility(1);
        this.workspaceManager!.codeRunner.unpause();
        this.workspaceManager!.stopAll();
        this.world.resetSprites();
        this.levelManager!.reset();
        this.workspaceManager!.api.sensing.ignoreAllKeyInputs(false);
        this.analytics.addProjectResetCount();
    };

    newSprite = () => {
        Logging.info("Adding new sprite.");
        const { t } = this.props;
        let sprite = new Sprite("Cubi");
        if (!this.world.centerAsOrigin) {
            sprite.x = this.world.container.width / 2
            sprite.y = this.world.container.height / 2
        }
        if (!LevelManager.isBuildMode()) {
            sprite.deletable = true;
        }
        if (Restore.costumeAddStatus !== RestoreCostumeAddStatus.RESET) {
            Restore.costumeAddStatus = RestoreCostumeAddStatus.SPRITE;
        }
        sprite.addCostumeUrl("Cubi", environment.CONTENT_FOLDER + `/it4kids/costumes/People/${(new Date()).getMonth() === 11 ? 'Weihnachten ' : ''}Cubi.png`);
        this.workspaceManager!.new(sprite);
        this.setSelectedSprite(sprite);
        notifyUser(t('SpriteEditor.NOTIFY_NEW_SPRITE'), { variant: 'success' });
    };

    duplicateSprite = () => {
        if (this.state.sprite && this.workspaceManager && this.workspaceManager.activeWorkspace) {
            const { t } = this.props;
            Logging.info("Duplicating currently selected sprite.");
            let copy = Sprite.deserialize(this.state.sprite.serialize());
            //Set ID to -1 so that it is updated to a free id.
            copy.id = -1;
            copy.updateCostume();
            copy.x += 20;
            copy.y -= 20;
            let workspaceState = this.workspaceManager.activeWorkspace.serialize();
            let toolbox = new Toolbox(workspaceState.toolbox);
            let duplicateWS = this.workspaceManager!.new(copy, toolbox);
            duplicateWS.init(workspaceState);
            Restore.createRestorePoint(translate('RestoreMessages.sprite.cloned') + this.state.sprite.name);
            this.setSelectedSprite(copy);
            notifyUser(t('SpriteEditor.NOTIFY_DUPLICATE_SPRITE'), { variant: 'success' });
        }
    };

    deleteSprite = (sprite: Sprite) => {
        Logging.info("Deleting sprite.");
        if (!sprite.isStaticBackground) {
            const { t } = this.props;
            this.world.removeSpriteById(sprite.id);
            this.workspaceManager!.removeWorkspaceById(sprite.id);
            Restore.createRestorePoint(translate('RestoreMessages.sprite.deleted') + sprite.name);
            (window as any).renderUtils.generateColorPalette();
            notifyUser(t('SpriteEditor.NOTIFY_DELETE_SPRITE'), { variant: 'success' });
        }
    };

    setAllStartPositions = async () => {
        const { t } = this.props;
        await this.world.sprites.forEach((s: Sprite) => {
            if (!s.isStaticBackground) {
                this.setStartConfig(s)
            }
        })
        notifyUser(t('SpriteEditor.NOTIFY_ALL_START_POSITIONS_SET'), { variant: 'success' });
    }

    setStartConfig = (sprite: Sprite) => {
        sprite.setStartConfiguration();
    };

    newLevel = () => {
        Logging.info("Starting new level.");
        this.levelManager!.empty();
        let background = new Sprite("Hintergrund", -1, this.world.getCenter());
        Restore.blockedReset = true;
        Restore.costumeAddStatus = RestoreCostumeAddStatus.RESET;
        background.addCostumeUrl("Weiß", environment.CONTENT_FOLDER + "/it4kids/backgrounds/Leer.png");
        this.workspaceManager!.new(background);
        LevelManager.levelTitle = 'Level';
        LevelManager.levelTitleUpdate = true;
        LevelManager.tutorial = {
            currentPageIndex: 0,
            pages: [],
            tutorialActive: false,
        };
        this.newSprite();
        this.world.updateOrigin(false);
        window.history.pushState({}, document.title, '?lang=' + i18n.language);
    };

    onResizeSplitPanel = () => {
        this.workspaceManager!.onResize();
        this.pixiApp.current!.windowResized();
    };

    setSelectedSprite = (sprite: Sprite | null) => {
        this.setState({ sprite: sprite });
        SpriteEditorInstance.readNewValues();
    };

    render() {
        let splitSizeDefault = "34%";
        if (this.view === "embedded") {
            splitSizeDefault = "45%";
        }
        return (
            <>
                <LoadingOverlay />
                <div style={{ minHeight: "600px", height: "100%", display: "flex", flexFlow: "column" }}>
                    <NewVariableModal />
                    <RenameVariableModal />
                    <GamemasterModal />
                    <Hidden smUp={true}>
                        <div style={{
                            padding: "10px",
                            opacity: 1,
                            zIndex: 10000,
                            textAlign: 'center',
                            position: 'absolute',
                            right: 0,
                            left: 0,
                            top: 0,
                            bottom: 0,
                            background: "#1961ac",
                            color: "white"
                        }}>
                            Die Lehrsoftware Cubi von IT4Kids funktioniert aktuell nur auf Android-Tablets, iPads oder PC.
                        </div>
                    </Hidden>
                    <header>
                        <MenuAppBar
                            playing={this.state.playing}
                            runProject={this.runProject}
                            pauseProject={this.pauseProject}
                            resetProject={this.resetProject}
                            newSprite={this.newSprite}
                            duplicateSprite={this.duplicateSprite}
                            newLevel={this.newLevel}
                            setAllStartPositions={this.setAllStartPositions}
                        />
                    </header>
                    <main style={{ flexGrow: 1, overflow: "auto", height: "100%" }}>
                        <SplitPane
                            split="vertical"
                            minSize={300}
                            maxSize={-10}
                            primary={"second"}
                            defaultSize={splitSizeDefault}
                            onChange={this.onResizeSplitPanel}
                            style={{ position: "relative" }}
                        >
                            <div id="blocklyArea" style={fullHeightStyle} />
                            <div id="playArea" style={{ height: "100%", display: "flex", flexDirection: "column" }}>
                                <PixiApp ref={this.pixiApp} world={this.world} />
                                <SpriteEditor onSpriteSelected={this.setSelectedSprite} deleteSprite={this.deleteSprite}
                                    setStartConfig={this.setStartConfig} sprite={this.state.sprite} />
                            </div>
                        </SplitPane>
                    </main>
                </div>
            </>
        );
    }
}

export default withTranslation()(withWidth()(withSnackbar(AppRoot)));
